import { ICacheStorage, CacheEntry } from './types'

const DB_NAME = 'aura_cache'
const STORE_NAME = 'responses'
const DB_VERSION = 1

export class IndexedDBCache implements ICacheStorage {
  private dbPromise: Promise<IDBDatabase> | null = null
  private userId: string | number | undefined

  private prefixKey (key: string): string {
    if (this.userId == null) return key
    return `user:${this.userId}:${key}`
  }

  setUserId (userId: string | number | undefined): void {
    this.userId = userId
  }

  private openDB (): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' })
        }
      }

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => {
        this.dbPromise = null
        reject(request.error)
      }
    })

    return this.dbPromise
  }

  private async tx (mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.openDB()
    return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME)
  }

  private request<T> (req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  async get<T = unknown> (key: string): Promise<CacheEntry<T> | null> {
    const store = await this.tx('readonly')
    const prefixed = this.prefixKey(key)
    const entry = await this.request<CacheEntry<T> | undefined>(store.get(prefixed))
    if (!entry) return null
    return { ...entry, key }
  }

  async set<T = unknown> (key: string, value: T): Promise<void> {
    const store = await this.tx('readwrite')
    const entry: CacheEntry<T> = { key: this.prefixKey(key), value, timestamp: Date.now() }
    await this.request(store.put(entry))
  }

  async remove (key: string): Promise<void> {
    const store = await this.tx('readwrite')
    await this.request(store.delete(this.prefixKey(key)))
  }

  async clear (): Promise<void> {
    const store = await this.tx('readwrite')
    await this.request(store.clear())
  }

  async has (key: string): Promise<boolean> {
    const entry = await this.get(key)
    return entry !== null
  }
}
